import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackShare } from './GoogleAnalytics';

/**
 * ShareButton - Social sharing for products and gallery images
 * 
 * Features:
 * - Native Web Share API on mobile (best UX)
 * - Fallback menu on desktop with Facebook, copy link
 * - Tracks shares via console (can add analytics later)
 */

interface ShareButtonProps {
  title: string;
  text?: string;
  url?: string;
  imageUrl?: string;
  className?: string;
  size?: 'sm' | 'md';
}

export default function ShareButton({ 
  title, 
  text, 
  url, 
  // imageUrl reserved for future Pinterest/social sharing with images
  imageUrl: _imageUrl,
  className = '',
  size = 'md'
}: ShareButtonProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build the share URL (current page or specific URL)
  const shareUrl = url || window.location.href;
  const shareText = text || `Check out ${title} from Nanny B's Bakery!`;

  // Check if Web Share API is available (mostly mobile)
  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;

  // Handle native share (mobile)
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: `${title} - Nanny B's Bakery`,
        text: shareText,
        url: shareUrl,
      });
      trackShare('product', title, 'native');
    } catch (err) {
      // User cancelled or error - fall back to menu
      if ((err as Error).name !== 'AbortError') {
        setShowMenu(true);
      }
    }
  };

  // Handle Facebook share
  const handleFacebookShare = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(fbUrl, '_blank', 'width=600,height=400');
    setShowMenu(false);
    trackShare('product', title, 'facebook');
  };

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackShare('product', title, 'copy_link');
      setTimeout(() => {
        setCopied(false);
        setShowMenu(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Handle button click
  const handleClick = () => {
    if (canNativeShare) {
      handleNativeShare();
    } else {
      setShowMenu(!showMenu);
    }
  };

  const sizeClasses = size === 'sm' 
    ? 'p-1.5 text-sm' 
    : 'p-2';

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <div className={`relative ${className}`}>
      {/* Share Button */}
      <button
        onClick={handleClick}
        className={`${sizeClasses} rounded-lg bg-white/80 hover:bg-white text-gray-600 hover:text-primary-600 transition-all shadow-sm hover:shadow-md`}
        title="Share"
        aria-label={`Share ${title}`}
      >
        <svg 
          className={iconSize}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" 
          />
        </svg>
      </button>

      {/* Share Menu (Desktop fallback) */}
      <AnimatePresence>
        {showMenu && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setShowMenu(false)}
            />
            
            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[180px]"
            >
              {/* Facebook */}
              <button
                onClick={handleFacebookShare}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-[#1877F2] flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">Facebook</span>
              </button>

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {copied ? (
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )}
                </div>
                <span className="text-gray-700 font-medium">
                  {copied ? 'Copied!' : 'Copy Link'}
                </span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

